// Zero-Knowledge Crypto Wrapper using Web Crypto API
// SECURITY NOTE: This file is served to the client. It contains NO secrets, API keys, or backend URLs.

import { BIP39_WORDLIST } from './wordlist.js';

const PBKDF2_ITERATIONS = 600000;

// There is deliberately NO password-to-master-key derivation in this file.
// The master key is CSPRNG (generateMasterBits). The password only derives a KEK
// (deriveKekFromPassword) which unwraps the server-stored envelope locally.

/** Generate a cryptographically random 32-byte master key (CSPRNG). */
export function generateMasterBits() {
    return globalThis.crypto.getRandomValues(new Uint8Array(32));
}

/**
 * Import raw 32 bytes as a non-extractable AES-GCM session key.
 *
 * Every way into the vault (setup, password, recovery phrase, password reset) ends here,
 * because only here are the raw master bits in memory. That is why the keyring is derived
 * here as well: one place, so a vault opened via recovery cannot behave differently.
 */
export async function importMasterKeyFromBits(bits) {
    const masterKey = await globalThis.crypto.subtle.importKey(
        "raw", bits, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]
    );
    vaultKeyrings.set(masterKey, await deriveVaultKeyring(bits));
    return masterKey;
}

// ---------------------------------------------------------------------------------------
// Keyring. Two further keys derived from the master bits with HKDF, each with its own
// info string, so neither can stand in for the master key or for the other:
//   indexKey     encrypts file and folder names in this browser's local storage
//   deleteProof  Ed25519 pair; the server holds only the public half and accepts a
//                deletion only with a signature from the private half
// The keyring hangs on the master key object (WeakMap). Whoever holds the master key
// can reach it, and it disappears together with the master key when the vault locks.
// ---------------------------------------------------------------------------------------
const vaultKeyrings = new WeakMap();

export function getVaultKeyring(masterKey) {
    return masterKey ? vaultKeyrings.get(masterKey) || null : null;
}

async function deriveVaultKeyring(bits) {
    const enc = new TextEncoder();
    const subtle = globalThis.crypto.subtle;
    const ikm = await subtle.importKey("raw", bits, "HKDF", false, ["deriveBits", "deriveKey"]);
    const hkdf = (info) => ({ name: "HKDF", hash: "SHA-256", salt: new Uint8Array(32), info: enc.encode(info) });

    const indexKey = await subtle.deriveKey(
        hkdf("tresor-local-index-v1"), ikm, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]
    );

    let deleteProof = null;
    const seed = new Uint8Array(await subtle.deriveBits(hkdf("tresor-delete-proof-v1"), ikm, 256));
    try {
        deleteProof = await ed25519FromSeed(seed);
    } catch (_) {
        // Browser without Ed25519: the vault opens, deleting is refused with a clear message.
    } finally {
        seed.fill(0);
    }
    return { indexKey, deleteProof };
}

async function ed25519FromSeed(seed) {
    const pkcs8 = new Uint8Array(ED25519_PKCS8_PREFIX.length + 32);
    pkcs8.set(ED25519_PKCS8_PREFIX);
    pkcs8.set(seed, ED25519_PKCS8_PREFIX.length);
    try {
        // extractable only for the JWK export of the public half; a second, non-extractable
        // import is what stays in memory for signing.
        const exportable = await globalThis.crypto.subtle.importKey('pkcs8', pkcs8, { name: 'Ed25519' }, true, ['sign']);
        const jwk = await globalThis.crypto.subtle.exportKey('jwk', exportable);
        if (!jwk?.x) throw new Error('ED25519_EXPORT_FAILED');
        const privateKey = await globalThis.crypto.subtle.importKey('pkcs8', pkcs8, { name: 'Ed25519' }, false, ['sign']);
        return { privateKey, publicKeyBase64url: jwk.x };
    } finally {
        pkcs8.fill(0);
    }
}

/** Signed text for a deletion, character for character identical to the backend. */
export function buildDeleteProofPayload({ userId, challenge, issuedAt, action, uploadIds }) {
    const ids = [...uploadIds].map(String).sort().join(',');
    return ['v1', 'tresor-delete', String(userId), challenge, String(issuedAt), action, ids].join(':');
}

export async function signDeleteProof(masterKey, payload) {
    const proof = getVaultKeyring(masterKey)?.deleteProof;
    if (!proof) throw new Error('DELETE_PROOF_UNAVAILABLE');
    const sig = await globalThis.crypto.subtle.sign('Ed25519', proof.privateKey, new TextEncoder().encode(payload));
    return toBase64url(new Uint8Array(sig));
}

// ---------------------------------------------------------------------------------------
// One key per file (format v2). A random 256-bit file key encrypts the chunks; the master
// key only encrypts that file key ("wrapped"), bound to the file's encryptionId via AAD,
// so a wrapped key cannot be moved onto another file. Files without a wrapped key are the
// old format and keep using the master key directly.
// ---------------------------------------------------------------------------------------
const fileKeyCache = new WeakMap();

function bytesToB64(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}

function b64ToBytes(b64) {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

const dekAad = (encryptionId) => new TextEncoder().encode(`tresor-dek-v1:${encryptionId}`);

export async function createFileKey(masterKey, encryptionId) {
    const bits = globalThis.crypto.getRandomValues(new Uint8Array(32));
    try {
        const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
        const ct = await globalThis.crypto.subtle.encrypt(
            { name: "AES-GCM", iv, additionalData: dekAad(encryptionId) }, masterKey, bits
        );
        const combined = new Uint8Array(12 + ct.byteLength);
        combined.set(iv);
        combined.set(new Uint8Array(ct), 12);
        const fileKey = await globalThis.crypto.subtle.importKey(
            "raw", bits, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]
        );
        return { fileKey, wrappedFileKey: bytesToB64(combined) };
    } finally {
        bits.fill(0);
    }
}

export async function openFileKey(masterKey, encryptionId, wrappedFileKey) {
    const combined = b64ToBytes(wrappedFileKey);
    const bits = new Uint8Array(await globalThis.crypto.subtle.decrypt(
        { name: "AES-GCM", iv: combined.slice(0, 12), additionalData: dekAad(encryptionId) },
        masterKey,
        combined.slice(12)
    ));
    try {
        return await globalThis.crypto.subtle.importKey(
            "raw", bits, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]
        );
    } finally {
        bits.fill(0);
    }
}

/** The key that encrypts this file's chunks: its own file key, or the master key (old format). */
export async function fileKeyFor(masterKey, fileMeta) {
    if (!fileMeta?.wrappedFileKey) return masterKey;
    let cache = fileKeyCache.get(masterKey);
    if (!cache) {
        cache = new Map();
        fileKeyCache.set(masterKey, cache);
    }
    let key = cache.get(fileMeta.encryptionId);
    if (!key) {
        key = await openFileKey(masterKey, fileMeta.encryptionId, fileMeta.wrappedFileKey);
        cache.set(fileMeta.encryptionId, key);
    }
    return key;
}

// ---------------------------------------------------------------------------------------
// Local index. Names and types of files and folders are stored in this browser only as
// ciphertext under the indexKey. Without the unlocked vault they cannot be read here either.
// ---------------------------------------------------------------------------------------
const indexAad = () => new TextEncoder().encode('tresor-local-index-v1');

export async function sealIndexFields(indexKey, fields) {
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
    const ct = await globalThis.crypto.subtle.encrypt(
        { name: "AES-GCM", iv, additionalData: indexAad() }, indexKey,
        new TextEncoder().encode(JSON.stringify(fields))
    );
    const combined = new Uint8Array(12 + ct.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(ct), 12);
    return bytesToB64(combined);
}

export async function openIndexFields(indexKey, sealed) {
    const combined = b64ToBytes(sealed);
    const pt = await globalThis.crypto.subtle.decrypt(
        { name: "AES-GCM", iv: combined.slice(0, 12), additionalData: indexAad() }, indexKey, combined.slice(12)
    );
    return JSON.parse(new TextDecoder().decode(pt));
}

/**
 * Derive a Key-Encryption-Key (KEK) from the recovery mnemonic.
 * Separate (recovery) salt so it is independent from the password salt.
 */
export async function deriveKekFromMnemonic(mnemonic, recoverySalt) {
    const enc = new TextEncoder();
    const keyMaterial = await globalThis.crypto.subtle.importKey(
        "raw", enc.encode(mnemonic.trim()), { name: "PBKDF2" }, false, ["deriveKey"]
    );
    return await globalThis.crypto.subtle.deriveKey(
        { name: "PBKDF2", salt: enc.encode(recoverySalt), iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
        keyMaterial,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"]
    );
}

/** KEK for password-wrapped master key storage (same PBKDF2 envelope as recovery). */
export async function deriveKekFromPassword(password, salt) {
    return deriveKekFromMnemonic(password, salt);
}

/**
 * Unlock with password — verifies by unwrapping the server-stored password envelope.
 * Wrong passwords fail at AES-GCM decrypt (no silent fallback).
 */
export async function unlockWithPassword(password, salt, passwordWrapped, passwordIv) {
    if (!passwordWrapped || !passwordIv) {
        throw new Error('PASSWORD_ENVELOPE_MISSING');
    }
    const kek = await deriveKekFromPassword(password, salt);
    const masterBits = await unwrapMasterBits(passwordWrapped, passwordIv, kek);
    return importMasterKeyFromBits(masterBits);
}

const toHex = (bytes) => Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
const fromHex = (hex) => new Uint8Array(hex.match(/.{1,2}/g).map(h => parseInt(h, 16)));
const toBase64url = (bytes) => btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');

/**
 * Domain-separated key used only to prove knowledge of the Recovery-Phrase to
 * the server during password reset. It cannot decrypt vault data.
 */
export async function deriveRecoveryProofKey(mnemonic, recoverySalt) {
    const enc = new TextEncoder();
    const keyMaterial = await globalThis.crypto.subtle.importKey(
        "raw", enc.encode(mnemonic.trim()), { name: "PBKDF2" }, false, ["deriveBits"]
    );
    const bits = await globalThis.crypto.subtle.deriveBits(
        { name: "PBKDF2", salt: enc.encode(`recovery-proof:${recoverySalt}`), iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
        keyMaterial,
        256
    );
    return new Uint8Array(bits);
}

export function exportRecoveryProofKey(proofKey) {
    return toBase64url(proofKey);
}

/**
 * Ed25519-Schluesselpaar aus derselben abgeleiteten Zahl wie bisher.
 *
 * Warum ueberhaupt: frueher ging der abgeleitete Wert selbst an den Server und war dort
 * zugleich der Schluessel zum Unterschreiben. Wer die Datenbank lesen konnte, konnte damit
 * einen gueltigen Nachweis bauen. Jetzt geht nur der oeffentliche Teil raus. Aus dem laesst
 * sich keine Unterschrift herstellen, die Datenbankzeile ist damit wertlos.
 *
 * Ableitung bleibt deterministisch: dieselben 12 Woerter und dasselbe Salz ergeben immer
 * denselben Schluessel. Es muss also nichts zusaetzlich gespeichert werden.
 *
 * Zur PKCS8-Huelle: WebCrypto nimmt einen privaten Ed25519-Schluessel nur in diesem Format
 * entgegen, nicht als rohe 32 Byte. Der Vorsatz ist die feste DER-Struktur aus RFC 8410.
 */
const ED25519_PKCS8_PREFIX = new Uint8Array([
    0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06,
    0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20
]);

export async function deriveRecoveryProofKeyPair(mnemonic, recoverySalt) {
    const seed = await deriveRecoveryProofKey(mnemonic, recoverySalt);
    const pkcs8 = new Uint8Array(ED25519_PKCS8_PREFIX.length + 32);
    pkcs8.set(ED25519_PKCS8_PREFIX);
    pkcs8.set(seed, ED25519_PKCS8_PREFIX.length);

    // extractable: true ist noetig, weil WebCrypto den oeffentlichen Teil nur ueber den
    // JWK-Export herausgibt. Der Schluessel existiert nur waehrend dieses Aufrufs, das
    // Ausgangsmaterial liegt ohnehin schon im selben Speicher.
    const privateKey = await globalThis.crypto.subtle.importKey(
        'pkcs8', pkcs8, { name: 'Ed25519' }, true, ['sign']
    );
    const jwk = await globalThis.crypto.subtle.exportKey('jwk', privateKey);
    if (!jwk?.x) throw new Error('ED25519_EXPORT_FAILED');
    return { privateKey, publicKeyBase64url: jwk.x };
}

/** Kann dieser Browser Ed25519? Aeltere Fassungen koennen es nicht, dann bleibt es bei HMAC. */
let ed25519SupportCache = null;
export async function supportsEd25519() {
    if (ed25519SupportCache !== null) return ed25519SupportCache;
    try {
        const probe = new Uint8Array(ED25519_PKCS8_PREFIX.length + 32);
        probe.set(ED25519_PKCS8_PREFIX);
        probe.set(globalThis.crypto.getRandomValues(new Uint8Array(32)), ED25519_PKCS8_PREFIX.length);
        const key = await globalThis.crypto.subtle.importKey('pkcs8', probe, { name: 'Ed25519' }, true, ['sign']);
        const jwk = await globalThis.crypto.subtle.exportKey('jwk', key);
        ed25519SupportCache = !!jwk?.x;
    } catch (_) {
        ed25519SupportCache = false;
    }
    return ed25519SupportCache;
}

/** Nutzlast fuer den Wechsel des Nachweis-Verfahrens, zeichengleich zum Backend. */
export function buildRecoveryProofUpgradePayload({ userId, challenge, issuedAt, newProofKey }) {
    return ['v1', 'upgrade-recovery-proof', String(userId), challenge, String(issuedAt), newProofKey].join(':');
}

export async function signRecoveryResetProofEd25519(privateKey, payload) {
    const sig = await globalThis.crypto.subtle.sign(
        'Ed25519', privateKey, new TextEncoder().encode(payload)
    );
    return toBase64url(new Uint8Array(sig));
}

export function buildRecoveryResetProofPayload({ userId, challenge, issuedAt, salt, passwordWrapped, passwordIv }) {
    return ['v1', 'reset-password', String(userId), challenge, String(issuedAt), salt, passwordWrapped, passwordIv].join(':');
}

export async function signRecoveryResetProof(proofKey, payload) {
    const enc = new TextEncoder();
    const hmacKey = await globalThis.crypto.subtle.importKey(
        "raw",
        proofKey,
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
    );
    const signature = await globalThis.crypto.subtle.sign("HMAC", hmacKey, enc.encode(payload));
    return toBase64url(new Uint8Array(signature));
}

/**
 * Huellenschluessel aus dem PRF-Geheimnis eines Passkeys (WebAuthn-Erweiterung "prf").
 * Das Geheimnis sind 32 Bytes, die nur das Geraet aus Passkey und Salz erzeugt; kein Passwort,
 * also kein Raten moeglich. HKDF mit eigenem info-String, getrennt von allen anderen Schluesseln.
 */
export async function passkeyKekFromPrf(prfOutput) {
    const ikm = await globalThis.crypto.subtle.importKey("raw", prfOutput, "HKDF", false, ["deriveKey"]);
    return globalThis.crypto.subtle.deriveKey(
        { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(32), info: new TextEncoder().encode("tresor-passkey-kek-v1") },
        ikm, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]
    );
}

/** Wrap (encrypt) the raw master bits under the recovery KEK. Returns hex strings. */
export async function wrapMasterBits(masterBits, kek) {
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
    const ct = await globalThis.crypto.subtle.encrypt({ name: "AES-GCM", iv }, kek, masterBits);
    return { wrappedHex: toHex(new Uint8Array(ct)), ivHex: toHex(iv) };
}

/** Unwrap (decrypt) the master bits with the recovery KEK. Throws on wrong mnemonic. */
export async function unwrapMasterBits(wrappedHex, ivHex, kek) {
    const ct = fromHex(wrappedHex);
    const iv = fromHex(ivHex);
    const bits = await globalThis.crypto.subtle.decrypt({ name: "AES-GCM", iv }, kek, ct);
    return new Uint8Array(bits);
}

/**
 * Generate a proper BIP39 12-word mnemonic (128-bit entropy + 4-bit checksum).
 * Only generated at setup step 3 — never on initial page load.
 */
export async function generateMnemonic() {
    const entropy = globalThis.crypto.getRandomValues(new Uint8Array(16));
    const hash = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', entropy));
    const bits = [];

    for (let i = 0; i < entropy.length; i++) {
        for (let j = 7; j >= 0; j--) bits.push((entropy[i] >> j) & 1);
    }
    for (let j = 7; j >= 4; j--) bits.push((hash[0] >> j) & 1);

    const words = [];
    for (let i = 0; i < 12; i++) {
        let idx = 0;
        for (let j = 0; j < 11; j++) idx = (idx << 1) | bits[i * 11 + j];
        words.push(BIP39_WORDLIST[idx]);
    }
    return words.join(' ');
}

export function normalizeMnemonic(phrase) {
    return phrase.trim().toLowerCase().split(/\s+/).filter(Boolean).join(' ');
}

/** Validate BIP39 structure: 12 known words. Wrong phrases fail at unwrap/decrypt anyway. */
export function isValidMnemonic(phrase) {
    const words = normalizeMnemonic(phrase).split(' ');
    if (words.length !== 12) return false;
    return words.every(w => BIP39_WORDLIST.includes(w));
}

/**
 * Prueft die BIP39-Pruefsumme (die letzten 4 der 132 Bit).
 *
 * Wozu: isValidMnemonic sieht nur, ob zwoelf bekannte Woerter dastehen. Ein einzelnes
 * vertipptes, aber gueltiges Wort kommt damit durch und scheitert erst beim Entschluesseln
 * mit "Entschluesselung fehlgeschlagen" — einer Meldung, die den Nutzer glauben laesst,
 * seine Dateien seien weg. Die Pruefsumme faengt 15 von 16 solcher Vertipper vorher ab.
 *
 * Sicher fuer Bestandskunden: generateMnemonic() erzeugt die Pruefsumme nach genau diesem
 * Verfahren (gegen die BIP39-Testvektoren geprueft), jede echte Phrase besteht sie.
 */
export async function hasValidMnemonicChecksum(phrase) {
    const words = normalizeMnemonic(phrase).split(' ');
    if (words.length !== 12) return false;

    const bits = [];
    for (const word of words) {
        const idx = BIP39_WORDLIST.indexOf(word);
        if (idx < 0) return false;
        for (let j = 10; j >= 0; j--) bits.push((idx >> j) & 1);
    }

    const entropy = new Uint8Array(16);
    for (let i = 0; i < 128; i++) {
        if (bits[i]) entropy[i >> 3] |= 1 << (7 - (i % 8));
    }
    const hash = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', entropy));
    for (let j = 0; j < 4; j++) {
        if (bits[128 + j] !== ((hash[0] >> (7 - j)) & 1)) return false;
    }
    return true;
}

export function mnemonicsMatch(a, b) {
    const left = normalizeMnemonic(a);
    const right = normalizeMnemonic(b);
    if (left.split(' ').length !== 12 || right.split(' ').length !== 12) return false;
    if (!isValidMnemonic(left) || !isValidMnemonic(right)) return false;
    return left === right;
}

// Encrypt a single chunk (for large files). AAD binds ciphertext to fileId + chunkIndex (P1-15).
export async function encryptChunk(arrayBuffer, key, fileId, chunkIndex) {
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
    const enc = new TextEncoder();
    const aad = enc.encode(`${fileId}:${chunkIndex}`);

    const encryptedContent = await globalThis.crypto.subtle.encrypt(
        { name: "AES-GCM", iv: iv, additionalData: aad },
        key,
        arrayBuffer
    );
    const combined = new Uint8Array(iv.length + encryptedContent.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedContent), iv.length);
    return combined;
}

// Decrypt a single chunk
export async function decryptChunk(combinedArrayBuffer, key, fileId, chunkIndex) {
    const iv = combinedArrayBuffer.slice(0, 12);
    const data = combinedArrayBuffer.slice(12);
    const enc = new TextEncoder();
    const aad = enc.encode(`${fileId}:${chunkIndex}`);

    return await globalThis.crypto.subtle.decrypt(
        { name: "AES-GCM", iv: new Uint8Array(iv), additionalData: aad },
        key,
        data
    );
}

// Encrypt a File Blob (Legacy wrapper for small files/compatibility)
export async function encryptBlob(blob, key, fileId) {
    const arrayBuffer = await blob.arrayBuffer();
    const encrypted = await encryptChunk(arrayBuffer, key, fileId, 0);
    return new Blob([encrypted], { type: 'application/octet-stream' });
}

// Decrypt a File Blob (iterates over all 5MB chunks; M6)
// Legacy path — prefer decryptBlobStream for large files (lower RAM peak).
export async function decryptBlob(encryptedBlob, key, originalType, fileId) {
    const combinedArrayBuffer = await encryptedBlob.arrayBuffer();
    const combinedArray = new Uint8Array(combinedArrayBuffer);

    const chunkSize = 5 * 1024 * 1024;
    const overhead = 12 + 16;
    const encryptedChunkSize = chunkSize + overhead;

    let offset = 0;
    let chunkIndex = 0;
    const parts = [];

    try {
        while (offset < combinedArray.length) {
            const remaining = combinedArray.length - offset;
            const currentEncryptedSize = Math.min(encryptedChunkSize, remaining);

            const chunkSegment = combinedArray.slice(offset, offset + currentEncryptedSize);
            const decryptedChunk = await decryptChunk(chunkSegment, key, fileId, chunkIndex);

            parts.push(new Uint8Array(decryptedChunk));

            offset += currentEncryptedSize;
            chunkIndex++;
        }

        return new Blob(parts, { type: originalType });
    } catch (e) {
        console.error("Decryption failed. Invalid key, corrupted data, or AAD mismatch.", e);
        throw new Error("Entschlüsselung fehlgeschlagen.");
    }
}

/**
 * Stream-decrypt a blob chunk-by-chunk so plaintext never fully accumulates in one array.
 * Falls back to decryptBlob when ReadableStream-backed Blobs are unavailable.
 */
export async function decryptBlobStream(encryptedBlob, key, originalType, fileId) {
    const combinedArrayBuffer = await encryptedBlob.arrayBuffer();
    const combinedArray = new Uint8Array(combinedArrayBuffer);

    const chunkSize = 5 * 1024 * 1024;
    const overhead = 12 + 16;
    const encryptedChunkSize = chunkSize + overhead;

    let offset = 0;
    let chunkIndex = 0;

    const stream = new ReadableStream({
        async pull(controller) {
            if (offset >= combinedArray.length) {
                controller.close();
                return;
            }
            const remaining = combinedArray.length - offset;
            const currentEncryptedSize = Math.min(encryptedChunkSize, remaining);
            const chunkSegment = combinedArray.slice(offset, offset + currentEncryptedSize);
            try {
                const decryptedChunk = await decryptChunk(chunkSegment, key, fileId, chunkIndex);
                controller.enqueue(new Uint8Array(decryptedChunk));
            } catch (e) {
                controller.error(new Error('Entschlüsselung fehlgeschlagen.'));
                return;
            }
            offset += currentEncryptedSize;
            chunkIndex++;
        }
    });

    try {
        // A ReadableStream is NOT a valid Blob part — `new Blob([stream])` would stringify it
        // to "[object ReadableStream]" (a 23-byte junk file). Response consumes the stream
        // properly into a Blob; re-wrap only to attach the original MIME type.
        if (typeof Response === 'function') {
            const streamed = await new Response(stream).blob();
            return originalType ? new Blob([streamed], { type: originalType }) : streamed;
        }
        return decryptBlob(encryptedBlob, key, originalType, fileId);
    } catch (_) {
        return decryptBlob(encryptedBlob, key, originalType, fileId);
    }
}
